import { Observable, throwError, of } from 'rxjs';
import { map, delay, tap } from 'rxjs/operators';
import { ServiceBase } from '../../interfaces/service-base.interface';
import { ListObject } from '../../models/list-object.model';
import { FactoryBase } from '../../interfaces/factory-base.interface';
import { ApiService } from '../../api/services/api.service';
import { FieldHandler } from '../../models/field-handler.model';
import { ListParameters } from '../../interfaces/list-parameters.interface';

export abstract class ImplementedService<T, R> implements ServiceBase<T> {
  constructor(
    protected readonly apiService: ApiService,
    public readonly factory: FactoryBase<T>,
    public readonly path: string
  ) {}

  protected mockData: T[];
  protected readonly mockDelay = 1000;

  protected getAllFields = new FieldHandler(true);
  protected getByIdFields = new FieldHandler(true);

  public getAll(routeParams: ListParameters): Observable<ListObject<T>> {
    this.checkParameters();

    if (!this.factory.loadFromResponse) {
      return throwError("The factory doesn't have the loadFromResponse implementation.");
    }

    if (this.mockData) {
      return of(this.mockData).pipe(
        delay(this.mockDelay),
        map(
          (data) => new ListObject<T>(data, data.length),
          tap((data) => console.log('Mock index', `GET ${this.path}`, data))
        )
      );
    }

    return this.apiService
      .getList<R>(`${this.path}`, { ...routeParams, fields: this.getAllFields.toString() })
      .pipe(
        map(
          (response) =>
            new ListObject<T>(response.data.map((i) => this.factory.loadFromResponse(i) as T), response.count)
        )
      );
  }

  public getById(id: number, routeParams?): Observable<T> {
    this.checkParameters();

    if (!this.factory.loadFromResponse) {
      return throwError("The factory doesn't have the loadFromResponse implementation.");
    }
    if (this.mockData) {
      return of(this.mockData).pipe(
        delay(this.mockDelay),
        map((data) => data[0]),
        tap((data) => console.log('Mock get', `GET ${this.path}/${id}`, data))
      );
    }
    return this.apiService
      .getOne<R>(`${this.path}/${id}`, routeParams)
      .pipe(map((response) => this.factory.loadFromResponse(response) as T));
  }

  public updateById(model: T, id: number): Observable<T> {
    this.checkParameters();

    if (!this.factory.toRequest) {
      return throwError("The factory doesn't have the toRequest implementation.");
    }
    if (this.mockData) {
      return of(this.mockData).pipe(
        delay(this.mockDelay),
        map((data) => data[0]),
        tap((data) => console.log('Mock update', `PUT ${this.path}/${id}`, this.factory.toRequest(model), data))
      );
    }
    return this.apiService
      .put<R>(`${this.path}/${id}`, this.factory.toRequest(model))
      .pipe(map((response) => this.factory.loadFromResponse(response) as T));
  }

  public store(model: T): Observable<T> {
    this.checkParameters();

    if (!this.factory.toRequest) {
      return throwError("The factory doesn't have the toRequest implementation.");
    }
    if (this.mockData) {
      return of(this.mockData).pipe(
        delay(this.mockDelay),
        map((data) => data[0]),
        tap((data) => console.log('Mock store', `POST ${this.path}`, this.factory.toRequest(model), data))
      );
    }
    return this.apiService
      .post<R>(`${this.path}`, this.factory.toRequest(model))
      .pipe(map((response) => this.factory.loadFromResponse(response) as T));
  }

  public delete(id: number): Observable<boolean> {
    this.checkParameters();
    if (this.mockData) {
      return of(true).pipe(
        delay(this.mockDelay),
        tap((data) => console.log('Mock delete', `DELETE ${this.path}/${id}`, data))
      );
    }

    return this.apiService.delete(`${this.path}/${id}`).pipe(map(() => true));
  }

  protected checkParameters() {
    if (!this.path || !this.factory) {
      return throwError("The service doesn't have some parameters.");
    }
  }
}
